/**
 * Design B Case Files.
 *
 * Desktop: the first photo-pair record pins under the header and scrolling
 * drives the dissolve from before (0) to after (1), with the meter and the
 * Before/After tags following along. Phones: no pin, the same progress
 * scrubs while the record crosses the screen. Other records simply reveal.
 */
import { scene, revealAll, ambient, ScrollTrigger, pinAnticipation, gsap, motionEnabled } from '@theme/clinic-motion';

const headerOffset = () =>
  parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 64;

// scene() only builds when one of its conditions (desktop, reduced motion)
// matches, so on a phone with motion allowed it never runs and the record
// sat on the before photo. Phones get the same scrub from a query of their
// own; reduced motion still falls back to the dissolve's slider.
const PHONE = '(max-width: 989.98px) and (prefers-reduced-motion: no-preference)';

class ClinicCaseFiles extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('video[data-clinic-ambient]').forEach(ambient);

    this.dispose = scene(this, ({ scroller, desktop }) => {
      revealAll(this, { scroller });
      return this.scrub({ scroller, desktop });
    });

    if (motionEnabled) {
      const mm = gsap.matchMedia();
      mm.add(PHONE, () => this.scrub({ scroller: window, desktop: false }));
      this.disposePhone = () => mm.revert();
    }
  }

  scrub({ scroller, desktop }) {
    const file = this.querySelector('[data-case-pinned]');
    if (!file) return;
    const stage = file.querySelector('[data-case-stage]');
    const dissolve = file.querySelector('clinic-dissolve');
    const meter = file.querySelector('[data-meter]');
    const tagBefore = file.querySelector('[data-tag-before]');
    const tagAfter = file.querySelector('[data-tag-after]');

    const apply = (progress) => {
      dissolve?.setProgress?.(progress);
      if (meter) meter.style.transform = `scaleX(${progress})`;
      if (tagBefore) tagBefore.style.opacity = String(1 - Math.min(1, progress * 1.6));
      if (tagAfter) tagAfter.style.opacity = String(Math.max(0, progress * 1.6 - 0.6));
    };
    apply(0);

    const trigger = desktop
      ? ScrollTrigger.create({
          trigger: stage,
          scroller,
          pin: stage,
          pinType: 'fixed',
          anticipatePin: pinAnticipation(),
          start: () => `top ${headerOffset() + 24}`,
          end: '+=120%',
          scrub: 0.4,
          invalidateOnRefresh: true,
          onUpdate: (self) => apply(self.progress),
        })
      : ScrollTrigger.create({
          trigger: stage,
          scroller,
          start: 'top 72%',
          // End on the photo, not the stage: the record below it makes the
          // stage tall on a phone, and the photo would finish under the header.
          endTrigger: dissolve || stage,
          end: 'bottom 45%',
          scrub: 0.4,
          onUpdate: (self) => apply(self.progress),
        });

    return () => {
      trigger.kill();
      apply(0.5);
    };
  }

  disconnectedCallback() {
    this.dispose?.();
    this.disposePhone?.();
  }
}

if (!customElements.get('clinic-case-files')) customElements.define('clinic-case-files', ClinicCaseFiles);
